import React, { useState, useEffect } from "react";

// react-router components
import { useLocation, Link, useNavigate } from "react-router-dom";

// @mui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import { Avatar } from "@mui/material";

// Argon Dashboard 2 MUI components
import Box from "themeComponents/Box";
import Typography from "themeComponents/Typography";

// Argon Dashboard 2 MUI example components
import Breadcrumbs from "themeComponents/Breadcrumbs";
import NotificationDropdown from "components/NotificationDropdown";
import { COMPCHECK_URL } from "configs/api";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarDesktopMenu,
  navbarMobileMenu,
} from "themeComponents/Navbars/DashboardNavbar/styles";

// Argon Dashboard 2 MUI context
import {
  useArgonController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

// Redux
import { useSelector } from "react-redux";
import { RootState } from "configs/store";

import useAuth from "hooks/useAuth";
interface DashboardNavbarProps {
  absolute?: boolean;
  light?: boolean;
  isMini?: boolean;
}

function DashboardNavbar({ absolute = false, light = true, isMini = false }: DashboardNavbarProps): React.JSX.Element {
  const { user } = useSelector((state: RootState) => state.auth);
  const { currentCourse } = useSelector((state: RootState) => state.courses);
  const [navbarType, setNavbarType] = useState<"static" | "sticky">("static");
  const [controller, dispatch] = useArgonController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator } = controller;
  const route = useLocation().pathname.split("/").slice(1);
  const pathname = useLocation().pathname;
  const navigator = useNavigate();

  const { isAdmin, isCompanyAdmin, isFellowAdmin } = useAuth();
  const getBreadcrumbData = React.useMemo(() => {
    const rawTitle = route[route.length - 1] || "dashboard";

    // Special named sub-pages under /courses
    if (pathname === "/courses/my-enrollments") {
      const isAdminView = isAdmin || isCompanyAdmin || isFellowAdmin;
      const enrollmentsTitle = isAdminView ? "Enrollments" : "My Enrollments";
      return { title: enrollmentsTitle, route: ["courses"] };
    }

    if (pathname === "/courses/explore") {
      return { title: "Explore Courses", route: ["courses"] };
    }

    if (pathname === "/courses/library") {
      return { title: "My Library", route: ["courses"] };
    }

    const courseMatch = pathname.match(/^\/courses\/([^/]+)(?:\/(play|edit|builder))?$/i);
    if (!courseMatch) {
      return { title: rawTitle, route };
    }

    const action = (courseMatch[2] || "").toLowerCase();
    const courseTitle = currentCourse?.title || "Course";
    if (!action) {
      return { title: courseTitle, route: ["courses"] };
    }

    if (action === "play") {
      return { title: `${courseTitle} Player`, route: ["courses"] };
    }
    if (action === "edit") {
      return { title: `${courseTitle} Edit`, route: ["courses"] };
    }
    if (action === "builder") {
      return { title: `${courseTitle} Builder`, route: ["courses"] };
    }

    return { title: rawTitle, route };
  }, [currentCourse?.title, pathname, route, isAdmin, isCompanyAdmin, isFellowAdmin]);

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar(): void {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /**
     The event listener that's calling the handleTransparentNavbar function when
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = (): void => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = (): void => setOpenConfigurator(dispatch, !openConfigurator);

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme: any) => navbar(theme, { transparentNavbar, absolute, light })}
    >
      <Toolbar sx={(theme: any) => (navbarContainer as any)(theme, { navbarType }) as any}>
        <Box
          color={light && transparentNavbar ? "white" : "dark"}
          mb={{ xs: 1, md: 0 }}
          sx={(theme: any) => navbarRow(theme, { isMini })}
        >
          <Breadcrumbs
            icon="home"
            title={getBreadcrumbData.title}
            route={getBreadcrumbData.route}
            light={transparentNavbar ? light : false}
          />
          <IconButton
            size="large"
            sx={{ ...navbarMobileMenu, color: 'white' } as any}
            onClick={handleMiniSidenav}
            edge="start"
            aria-label="open sidebar menu"
          >
            <Icon fontSize="medium" style={{ color: 'white' }}>{miniSidenav ? "menu_open" : "menu"}</Icon>
          </IconButton>
        </Box>
        {isMini ? null : (
          <Box sx={{
            ...((theme: any) => navbarRow(theme, { isMini })) as any,
            display: {
              xs: 'none',
              md: 'none',
              xl: 'flex',
            }
          }}>
            <Box color={light ? "white" : "inherit"}>
              <Link to="/profile" style={{ cursor: "pointer", textDecoration: "none" }}>
                <IconButton sx={navbarIconButton as any} size="large">
                  <Avatar src={user?.imageURL ? `${COMPCHECK_URL}/api/${user.imageURL}` : undefined} />
                  <Typography
                    variant="button"
                    fontWeight="medium"
                    color={(light && transparentNavbar ? "inherit" : "default") as any}
                    sx={{ ml: 1 }}
                  >
                    {user?.name || "User"}
                  </Typography>
                </IconButton>
              </Link>
              {/* <IconButton
                size="small"
                color={(light && transparentNavbar ? "inherit" : "default") as any}
                sx={navbarMobileMenu as any}
                onClick={handleMiniSidenav}
              >
                <Icon>{miniSidenav ? "menu_open" : "menu"}</Icon>
              </IconButton> */}
              {/* <IconButton
                size="small"
                color={(light && transparentNavbar ? "inherit" : "default") as any}
                sx={navbarIconButton as any}
                onClick={handleConfiguratorOpen}
              >
                <Icon>settings</Icon>
              </IconButton>
              <NotificationDropdown /> */}
            </Box>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
}

export default DashboardNavbar;

